import React, { useContext, useState } from "react";
import useFetch from "../../../../hooks/useFetch";
import {
  accessControlAdd,
  accessControlEdit,
  accessControlList,
} from "../../../../services/accessControlService";
import Checkbox from "../../../form/Checkbox";
import Table from "../../../elements/table/Table";
import useMutation from "../../../../hooks/useMutation";
import { toast } from "react-toastify";
import Button from "../../../form/Button";
import Modal from "../../../elements/Modal";
import useForm from "../../../../hooks/useForm";
import Select from "../../../form/Select";
import { apiRequest } from "../../../../util/apiUtils";
import { setPermission } from "../../../../redux/slice/permissionSlice";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import SocketContext from "../../../../providers/SocketContext";
import { removeDuplicateFromArray } from "../../../../helpers";
import MultiselectDropdown from "../../../form/MultiselectDropdown";
import { scholarshipList } from "../../../../services/scholarshipService";
import { useGetPermission } from "../../../../hooks/useGetPermission";
import { useDebounce } from "../../../../hooks/useDebounce";
import PopoverDropdown from "../../../elements/PopoverDropdown";

const validation = {
  name: { required: true, message: "Name is required" },
};

function getTableHeaderArr(res) {
  try {
    const maxTypeElement = res?.data?.permission
      ?.map((item) => Object.keys(item?.type))
      .flat(Infinity)
      ?.filter((item) => item !== "_id")
      ?.filter(
        (item, index, self) => index === self.findIndex((t) => t === item)
      );

    const toNormalString = (str) => str.replace(/([a-z])([A-Z])/g, "$1 $2");
    const tableHeaderArr = maxTypeElement?.map((key) => ({
      name: toNormalString(key),
      value: key,
    }));
    return { tableHeaderArr };
  } catch (error) {
    console.error(error);
  }
}

const initialState = {
  permission: [
    {
      name: "list",
      value: "list",
      checked: false,
    },
    {
      name: "add",
      value: "add",
      checked: false,
    },
    {
      name: "edit",
      value: "edit",
      checked: false,
    },
    {
      name: "details",
      value: "details",
      checked: false,
    },
    {
      name: "delete",
      value: "delete",
      checked: false,
    },
    {
      name: "change Status",
      value: "changeStatus",
      checked: false,
    },
    {
      name: "change Order",
      value: "changeOrder",
      checked: false,
    },
  ],
};

const ManageAccessControl = ({ data = {} }) => {
  const { socket } = useContext(SocketContext);
  const { permission, status } = useGetPermission("scholarships");
  const [createModal, setCreateModal] = useState({ isOpen: false });
  const [scholarshipFilters, setScholarshipFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const [scholarshipKeyword, setScholarshipkeyword] = useState("");
  const scholarshipSearch = useDebounce(scholarshipKeyword, 300);
  const [alradyExist, setAlreadyExist] = useState([
    // "dashboard",
    "siteSettings",
    "accessControl",
  ]);

  const {
    data: scholarshipData,
    error,
    isLoading,
    updateData,
    isFetching,
    refetch,
  } = useFetch({
    dependencies: ["scholarship", scholarshipFilters, permission?.list],
    fetchFunction: async () => {
      const res = await scholarshipList({
        ...scholarshipFilters,
        ...(scholarshipSearch && { keyword: scholarshipSearch }),
      });
      if (res?.docs?.status === 200) {
        return {
          ...res?.docs,
          data: removeDuplicateFromArray([
            ...(scholarshipData?.data || []),
            ...res?.docs?.data,
          ]),
        };
      } else {
        if (scholarshipData?.data?.length > 0) {
          return scholarshipData;
        } else {
          return res;
        }
      }
    },
    enabled: !!permission?.list && status === "ok",
  });

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    resetField,
    formValidate,
  } = useForm({
    validationRules: validation,
    initialState,
  });

  const {
    data: accessData,
    error: accessDataError,
    isLoading: accessDataLoading,
    isFetching: accessDataFetching,
    updateData: accessDataUpdate,
  } = useFetch({
    dependencies: ["accessControl", data?._id],
    fetchFunction: async () => {
      const res = await accessControlList({
        userId: data?._id,
      });
      if (res?.status === 200) {
        const { tableHeaderArr } = getTableHeaderArr(res);
        const newObj = {
          ...res,
          tableHeaderArr,
          data:
            res?.data?.permission?.map((item) => {
              return {
                _id: item?._id,
                data: { [item?.name]: item?.type },
                code: item?.code,
                docId: res?.data?._id,
              };
            }) || [],
        };
        const exist_data = [...(newObj?.data || [])?.map((item) => item?.code)];
        setAlreadyExist((pre) => [...pre, ...exist_data]);
        return newObj;
      } else {
        return res;
      }
    },
    enabled: !!data?._id,
  });

  const { data: menuList, updateData: updateMenuList } = useFetch({
    dependencies: ["menulist", accessData?.status],
    fetchFunction: async () => {
      const res = await apiRequest("get", "permission/sidebar");
      const already_exist_data_codes = [
        ...accessData?.data?.map((item) => item?.code),
        ...alradyExist,
      ];
      const update_data = res?.data?.data?.filter(
        (item) => !already_exist_data_codes?.includes(item?.code)
      );

      return { ...res?.data, data: update_data };
    },
    enabled: accessData?.status === 200,
  });

  const { mutate: handleCheckboxChange } = useMutation({
    mutationFn: async ({ row, key, checked }) => {
      const data_key = Object.keys(row.data)?.[0];
      const update_row = { ...(row.data[data_key] || {}) };
      delete update_row?._id;

      update_row[key] = checked;

      accessDataUpdate((pre) => {
        pre.data = pre?.data?.map((item) => {
          const data = item?.data || {};
          data[data_key] = update_row;

          return {
            ...item,
            data,
          };
        });
        return { ...pre };
      });

      const res = await accessControlEdit({
        userId: data?._id,
        docId: row?.docId,
        permission: { name: data_key, code: row?.code, type: update_row },
      });
      return { res, payload: { row, key, checked } };
    },
    onSuccess: ({ res, payload }) => {
      const { row, key, checked } = payload;
      const data_key = Object.keys(row.data)?.[0];
      const update_row = row.data[data_key];

      if (res?.status === 200) {
        socket.emit("permission", { res, userId: data?._id });
      } else {
        toast.warning(res?.message || res?.error);
        update_row[key] = !checked;
      }
    },
    onError: (err) => {
      console.log(err);
    },
  });

  const { mutate: addScholarshipToUser } = useMutation({
    mutationFn: async ({ row, key, value }) => {
      const data_key = Object.keys(row.data)?.[0];
      const previousValue = row.data[data_key]?.[key];
      const update_row = { ...(row.data[data_key] || {}) };

      delete update_row?._id;
      update_row[key] = value;

      accessDataUpdate((pre) => {
        pre.data = pre?.data?.map((item) => {
          const data = item?.data || {};
          data[data_key] = update_row;

          return {
            ...item,
            data,
          };
        });
        return { ...pre };
      });

      const res = await accessControlEdit({
        userId: data?._id,
        docId: row?.docId,
        permission: { name: data_key, code: row?.code, type: update_row },
      });
      return { res, payload: { row, key, value, previousValue } };
    },
    onSuccess: ({ res, payload }) => {
      const { row, key, value, previousValue } = payload;
      const data_key = Object.keys(row.data)?.[0];
      const update_row = row.data[data_key];

      if (res?.status === 200) {
        socket.emit("permission", {
          res: { ...res, type: "addScholarship" },
          userId: data?._id,
        });
      } else {
        toast.warning(res?.message || res?.error);
        update_row[key] = previousValue;
      }
    },
    onError: (err) => {
      console.log(err);
    },
  });

  const tableHeadData = [
    { _id: 1, name: "Permission Category", align: "left" },
    // { _id: 2, name: "Create", align: "center" },
    // { _id: 3, name: "Edit", align: "center" },
    // { _id: 4, name: "View", align: "center" },
    ...(accessData?.tableHeaderArr || [])?.map((item) => ({
      _id: item?.name,
      name: item?.name,
      align: "center",
    })),
  ];

  const tableData = accessData?.data?.map((item) => {
    const key = Object.keys(item?.data)?.[0];
    const find_scholarship_obj = accessData?.data?.filter(
      (item) => item?.code === "scholarships"
    );
    const assignScholarships =
      find_scholarship_obj && find_scholarship_obj?.length > 0
        ? Object.values(find_scholarship_obj?.[0]?.data)?.[0]?.assignScholarship
        : [];
    const selectedScholarships = scholarshipData?.data
      ?.filter((item) => assignScholarships?.includes(item?._id))
      ?.map((item) => item?.title || "");

    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          data: <div>{key}</div>,
        },
        ...(accessData?.tableHeaderArr || [])?.map((head) => {
          return {
            _id: head?.value,
            type: "text",
            align: "left",
            data:
              item?.data[key][head?.value] === true ||
              item?.data[key][head?.value] === false ? (
                <div className="flex justify-center">
                  <Checkbox
                    altView={true}
                    checked={item?.data[key][head?.value] || false}
                    onChange={() => {
                      handleCheckboxChange({
                        row: item,
                        key: head?.value,
                        checked: !item?.data[key][head?.value],
                      });
                    }}
                  />
                </div>
              ) : item?.code === "scholarships" ? (
                <div className="flex justify-center">
                  <MultiselectDropdown
                    label={"Select Scholarships"}
                    dropdownContainer="!min-w-80 !w-auto"
                    dropdownData={scholarshipData?.data?.map((item) => ({
                      name: item?.title,
                      value: item?._id,
                    }))}
                    values={item?.data[key]?.assignScholarship}
                    onChange={(val) => {
                      addScholarshipToUser({
                        row: item,
                        key: head?.value,
                        value: val,
                      });
                    }}
                    onScroll={(e) => {
                      loadData(e, scholarshipData, setScholarshipFilters);
                    }}
                  />
                </div>
              ) : item?.code === "scholarshipApp" ? (
                <div className="space-y-1">
                  {selectedScholarships?.length ? (
                    <PopoverDropdown
                      buttonLabel={
                        <div className="flex items-center gap-2">
                          Selected Scholarships{" "}
                          <span className="flex items-center justify-center bg-white text-chestnut-rose w-6 h-6 rounded-full text-xs !font-Gotham">
                            {selectedScholarships?.length || 0}
                          </span>
                        </div>
                      }
                    >
                      <ul>
                        {selectedScholarships?.map((str, i) => (
                          <li
                            key={i?.toString()}
                            className="relative flex items-center before:content-[''] before:absolute before:top-1/2 before:left-0 before:w-2 before:h-2 before:bg-black before:rounded-full before:-translate-y-1/2"
                          >
                            <span className="ml-3 text-sm">{str}</span>
                          </li>
                        ))}
                      </ul>
                    </PopoverDropdown>
                  ) : (
                    <div className="flex justify-center">-</div>
                  )}
                </div>
              ) : (
                <div className="flex justify-center">-</div>
              ),
          };
        }),
      ],
    };
  });

  const { mutate: onSubmit, isPending } = useMutation({
    mutationFn: async (val) => {
      const type = {};
      val?.permission?.forEach((item) => {
        type[item?.value] = item?.checked;
      });
      const payload = {
        userId: data?._id,
        permission: { name: val?.name, code: val?.code, type },
      };
      const res = await accessControlAdd(payload);
      return { res, payload };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 201 || res?.status === 200) {
        updateMenuList((pre) => {
          pre.data = pre.data?.filter(
            (item) => item?.code !== payload?.permission?.code
          );
          return { ...pre };
        });

        socket.emit("permission", { res, userId: data?._id });
        setAlreadyExist((pre) => [...pre, payload?.permission?.code]);
        accessDataUpdate((pre) => {
          const { tableHeaderArr } = getTableHeaderArr(res);
          const newData = res?.data?.permission?.map((item) => ({
            _id: item?._id,
            docId: res?.data?._id,
            code: item?.code,
            data: {
              [item?.name]: item?.type,
            },
          }));

          pre.data = newData || [];
          pre.tableHeaderArr = tableHeaderArr;
          return { ...pre };
        });
        setCreateModal({ isOpen: false });
        setFieldsValue({ ...initialState });
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (err) => {
      console.log(err);
    },
  });

  function loadData(e, obj, setData = () => {}) {
    if (
      Math.ceil(e.target.scrollTop) + 20 >=
        e.target.scrollHeight - e.target.clientHeight &&
      obj?.metadata?.hasNextPage
    ) {
      setData((pre) => ({
        ...pre,
        page: obj?.metadata?.nextPage,
      }));
    }
  }

  return (
    <div>
      <div className="relative w-full mb-2">
        <Button
          buttonClasses={
            "ml-auto !bg-transparent border border-transparent !h-10 hover:!bg-chestnut-rose/10 hover:!border-chestnut-rose/10"
          }
          buttonLabel={"Create New"}
          buttonLabelClasses={"font-medium !text-sm text-chestnut-rose"}
          buttonFunction={() => setCreateModal({ isOpen: true, data: null })}
        />
      </div>
      <Table
        tableClasses={"!shadow-none"}
        tableHeadData={tableHeadData}
        tableData={tableData}
        isLoder={accessDataLoading}
        isFetching={accessDataFetching}
      />
      <Modal
        title={"Add permission"}
        size="xl"
        isOpen={createModal?.isOpen}
        setIsOpen={() => setCreateModal({ isOpen: false })}
      >
        <form onSubmit={(event) => handleSubmit(event, onSubmit)}>
          <Select
            label={"Title"}
            placeholder="Select page"
            selectedValue={values?.code}
            dropdownData={(menuList?.data || [])?.map((item) => ({
              name: item?.label,
              value: item?.code,
            }))}
            setSelectedValue={(val, data) => {
              setFieldsValue({ ...values, name: data?.name, code: val });
            }}
            {...formValidate(errors, "name")}
          />
          <div className="py-4">
            <div className="pb-3 mb-3 border-b border-slate-300">
              <Checkbox
                checkboxLabel={"Select all"}
                checkboxLableClass={
                  "!text-base font-medium whitespace-nowrap capitalize cursor-pointer w-fit"
                }
                checked={values?.permission?.every((item) => item?.checked)}
                onChange={(e) => {
                  handleChange({
                    target: {
                      name: "permission",
                      value: values?.permission?.map((item) => ({
                        ...item,
                        checked: e.target.checked,
                      })),
                    },
                  });
                }}
              />
            </div>
            <ul className="grid grid-cols-3 gap-3">
              {values?.permission?.map((p, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Checkbox
                    checked={p?.checked}
                    checkboxName={p?.value}
                    value={p?.value}
                    onChange={(e) => {
                      handleChange({
                        target: {
                          name: "permission",
                          value: values?.permission?.map((item) =>
                            item?.value === e.target.value
                              ? { ...item, checked: e.target.checked }
                              : item
                          ),
                        },
                      });
                    }}
                    checkboxLabel={p?.name}
                    checkboxLableClass={
                      "!text-base font-medium whitespace-nowrap capitalize cursor-pointer"
                    }
                  />
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-3 flex items-center justify-center">
            <Button
              buttonType="submit"
              buttonLabel={"Create permission"}
              buttonClasses={
                "!rounded-full !px-8 !h-12 !border !border-chestnut-rose !bg-transparent !text-chestnut-rose hover:!bg-chestnut-rose hover:!text-white"
              }
              buttonLabelClasses={"mx-auto"}
              {...(isPending && {
                loading: true,
                buttonIconPosition: "left",
                isDisable: true,
              })}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ManageAccessControl;
